import React, { useState, useEffect } from 'react'
import { Button, FeatherLoader } from '@abroad/components'
import { Dropdown, Table } from 'react-bootstrap'
import {
  AppleIcon,
  GoogleIcon,
  MicrosoftIcon,
} from '../assetsComponents/common'
import {
  useUserService,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import API from '../../utils/API'
import AppleCalendarModal from '../common/AppleCalendarModal'
import { DeleteConfirmationPrompt } from '../common'

const AccountsTab = ({ connectedCalendars = [], onRefreshCalendars }) => {
  const [isConnectingAbroad, setIsConnectingAbroad] = useState(false)
  const [isConnectingPersonal, setIsConnectingPersonal] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showAppleModal, setShowAppleModal] = useState(false)
  const [abroadCalendars, setAbroadCalendars] = useState([])
  const [personalCalendars, setPersonalCalendars] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const [calendarToDisconnect, setCalendarToDisconnect] = useState(null)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const { user } = useUserService()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const userId = user?._id

  const getProviderLogo = (provider) => {
    switch (provider) {
      case 'google':
        return <GoogleIcon />
      case 'microsoft':
        return <MicrosoftIcon />
      case 'apple':
        return <AppleIcon />
      default:
        return <i className='fas fa-calendar-alt mr-2'></i>
    }
  }

  const handleConnectCalendar = (
    calendarType = 'google',
    isAbroadCalendar = false,
  ) => {
    if (isConnectingAbroad || isConnectingPersonal) return

    if (calendarType === 'apple') {
      setShowAppleModal(true)
      return
    }

    if (isAbroadCalendar) {
      setIsConnectingAbroad(true)
    } else {
      setIsConnectingPersonal(true)
    }

    window.open(
      `${process.env.REACT_APP_API_ENDPOINT}/calendar/authenticate/${calendarType}?isAbroadCalendar=${isAbroadCalendar}`,
      '_self',
    )
  }

  const handleCloseAppleModal = () => {
    setShowAppleModal(false)
  }

  const segregateCalendars = (calendars = []) => {
    const abroad = []
    const personal = []

    calendars.forEach((calendar) => {
      if (calendar?.isAbroadCalendar) {
        abroad.push(calendar)
      } else {
        personal.push(calendar)
      }
    })

    setAbroadCalendars(abroad)
    setPersonalCalendars(personal)
  }

  const fetchConnectedCalendars = async () => {
    if (!userId) return

    setIsLoading(true)
    try {
      const { data } = await API.AdminAPI.scheduling.getAllConnectedCalendars(
        userId,
      )
      segregateCalendars(data || [])
      setIsLoading(false)
    } catch (e) {
      Error.showError(e)
      setIsLoading(false)
    }
  }

  const handleAppleCalendarConnected = async () => {
    setShowAppleModal(false)
    // Refresh calendar list after Apple calendar is connected
    await fetchConnectedCalendars()
    // Also refresh parent's calendar connection status
    if (typeof onRefreshCalendars === 'function') {
      onRefreshCalendars()
    }
  }

  // Reset connecting states and fetch calendars when user becomes available
  useEffect(() => {
    // Reset connecting states on mount/remount (handles OAuth redirect return)
    setIsConnectingAbroad(false)
    setIsConnectingPersonal(false)

    // Fetch calendars when user becomes available (after OAuth redirect)
    if (user && userId) {
      fetchConnectedCalendars()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user])

  // Reset connecting states when window regains focus (handles OAuth redirect return)
  useEffect(() => {
    const handleFocus = () => {
      setIsConnectingAbroad(false)
      setIsConnectingPersonal(false)
      if (user && userId) {
        fetchConnectedCalendars()
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user])

  // Apple has no OAuth redirect, so reconnecting re-enters credentials in the modal
  const handleReconnectCalendar = (calendar) => {
    if (calendar.isActive !== false) return

    if (calendar.provider === 'apple') {
      setShowAppleModal(true)
      return
    }

    handleConnectCalendar(calendar.provider, false)
  }

  const handleDisconnectCalendar = (calendar) => {
    setCalendarToDisconnect(calendar)
    setShowDisconnectModal(true)
  }

  const confirmDisconnectCalendar = async () => {
    if (!calendarToDisconnect || !userId) return

    setIsDisconnecting(true)
    try {
      const credentialId =
        calendarToDisconnect._id || calendarToDisconnect.credentialId
      const { data } = await API.AdminAPI.scheduling.deleteCalendarCredential(
        userId,
        credentialId,
      )

      if (data?.status === 'success') {
        Notification.showNotification(
          data?.message || 'Account disconnected successfully',
          'success',
        )
        // Refresh calendar list after disconnect
        await fetchConnectedCalendars()
        // Also refresh parent's calendar connection status
        if (typeof onRefreshCalendars === 'function') {
          onRefreshCalendars()
        }
      }
      setShowDisconnectModal(false)
      setCalendarToDisconnect(null)
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsDisconnecting(false)
    }
  }

  return (
    <>
      <AppleCalendarModal
        showModal={showAppleModal}
        handleClose={handleCloseAppleModal}
        onCalendarConnected={handleAppleCalendarConnected}
      />

      <DeleteConfirmationPrompt
        showModal={showDisconnectModal}
        setShowModal={setShowDisconnectModal}
        isDeleteLoading={isDisconnecting}
        handleDelete={confirmDisconnectCalendar}
        message='Are you sure you want to disconnect this calendar account? This action cannot be undone.'
        buttonConfirmText='Disconnect'
      />

      <section className='mb-5'>
        <div className='d-flex justify-content-between align-items-center'>
          <h3 className='my-auto s3'>Your Abroad Calendar</h3>
          <Button
            variant='gray'
            size='md'
            className='line-height-normal text-uppercase border-radius-3'
            onClick={() => handleConnectCalendar('google', true)}
            disabled={
              isConnectingAbroad ||
              isConnectingPersonal ||
              abroadCalendars.length > 0
            }>
            {/* <GoogleIcon /> */}
            <span className='s8'>
              {isConnectingAbroad ? 'CONNECTING...' : 'CONNECT'}
            </span>
          </Button>
        </div>

        <Table responsive className='custom-table mt-3 mb-0'>
          <thead>
            <tr>
              <th className='text-uppercase sa2'>#</th>
              <th className='text-uppercase sa2'>Email</th>
            </tr>
          </thead>
          {isLoading && (
            <tr>
              <td className='text-center border-0 pt-3' colSpan='7'>
                <FeatherLoader />
              </td>
            </tr>
          )}
          {abroadCalendars?.length === 0 && !isLoading && (
            <tr>
              <td className='text-center s6 py-3' colSpan='7'>
                No accounts connected yet.
              </td>
            </tr>
          )}
          <tbody>
            {abroadCalendars?.length > 0 &&
              !isLoading &&
              abroadCalendars.map((calendar, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td className='d-flex align-items-center text-break'>
                    {/* {getProviderLogo(calendar.provider)} */}
                    <span>{calendar.email}</span>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </section>

      <section>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <div>
            <h3 className='my-auto s3 mb-2'>Your Personal Calendar</h3>
          </div>
          <Dropdown
            onToggle={(isOpen) => setDropdownOpen(isOpen)}
            className='personal-calendar-dropdown mt-3 mt-md-0'>
            <Dropdown.Toggle
              as={Button}
              variant='gray'
              className='d-flex align-items-center btn custom-dropdown-toggle'
              disabled={isConnectingAbroad || isConnectingPersonal}>
              <span className='mr-2'>
                {isConnectingPersonal ? 'Connecting' : 'Connect'}
              </span>
              <div className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='12'
                  viewBox='0 0 16 12'
                  fill='none'>
                  <path
                    d='M2.8086 4.00313L8.10542 8.20313'
                    stroke='white'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                  />
                  <path
                    d='M13.3984 4.00313L8.10156 8.20312'
                    stroke='white'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                  />
                </svg>
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu className='border border-dark dropdownmin py-0 w-100'>
              <Dropdown.Item
                onClick={() => handleConnectCalendar('google', false)}
                disabled={isConnectingAbroad || isConnectingPersonal}>
                <div className='d-flex align-items-center py-2'>
                  {getProviderLogo('google')}
                  <span className='sa2 ml-2'>Google</span>
                </div>
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleConnectCalendar('microsoft', false)}
                disabled={isConnectingAbroad || isConnectingPersonal}>
                <div className='d-flex align-items-center py-2'>
                  {getProviderLogo('microsoft')}
                  <span className='sa2 ml-2'>Microsoft</span>
                </div>
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleConnectCalendar('apple', false)}
                disabled={isConnectingAbroad || isConnectingPersonal}>
                <div className='d-flex align-items-center py-2'>
                  {getProviderLogo('apple')}
                  <span className='sa2 ml-2'>Apple</span>
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <p className='s6 text-muted mt-2 mb-0'>
          Only calendars switched on in My Calendars block client bookings. Each
          account's main calendar is on by default. Events marked "Free" never
          block, and Google and Outlook mark all-day events as Free, so set them
          to Busy to block the day.
        </p>
        <Table responsive className='custom-table mt-3 mb-0'>
          <thead>
            <tr>
              <th>#</th>
              <th>Email</th>
              <th>Platform</th>
              <th>Actions</th>
            </tr>
          </thead>
          {isLoading && (
            <tr>
              <td className='text-center border-0 pt-3' colSpan='7'>
                <FeatherLoader />
              </td>
            </tr>
          )}
          {personalCalendars?.length === 0 && !isLoading && !isLoading && (
            <tr>
              <td className='text-center s6 py-3' colSpan='4'>
                No accounts connected yet.
              </td>
            </tr>
          )}
          <tbody>
            {personalCalendars?.length > 0 &&
              !isLoading &&
              personalCalendars.map((calendar, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    {/* {getProviderLogo(calendar.provider)} */}
                    <span>{calendar.email}</span>
                  </td>
                  <td className='text-capitalize'>{calendar.provider}</td>
                  <td>
                    <div className='d-flex align-items-center'>
                      {calendar.isActive === false && (
                        <span
                          className='mr-3 text-saffron-700 cursor-pointer'
                          onClick={() => handleReconnectCalendar(calendar)}>
                          Reconnect
                        </span>
                      )}
                      <span
                        className='text-danger cursor-pointer'
                        onClick={() => handleDisconnectCalendar(calendar)}>
                        Disconnect
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </section>
    </>
  )
}

export default AccountsTab
